import { useQuery } from '@tanstack/react-query';
import { posBackendApi } from '@/lib/api-client';

/**
 * One row of the super-admin cross-tenant campaign message-usage rollup —
 * derived from the append-only message_usage ledger (pos-backend
 * campaign_message_usage_summary view). Used for SMS billing / statements.
 */
export interface CampaignUsageRow {
  campaignId: string;
  campaignName: string | null;
  tenantId: string;
  locationId: string | null;
  locationName: string | null;
  firstMessageAt: string | null;
  lastMessageAt: string | null;
  messagesAttempted: number;
  messagesSent: number;
  messagesFailed: number;
  billableSegments: number;
}

export interface CampaignUsageFilters {
  /** Restrict to one business; omit for all tenants. */
  tenantId?: string;
  /** ISO timestamp — include campaigns whose last send is on/after this. */
  from?: string;
  /** ISO timestamp — include campaigns whose first send is before this. */
  to?: string;
}

/**
 * Super-admin only. Calls the cross-tenant billing endpoint (no X-Tenant-Id —
 * the super_admin JWT resolves to all tenants server-side).
 */
export function useAdminCampaignUsage(filters: CampaignUsageFilters = {}) {
  const params: Record<string, string> = {};
  if (filters.tenantId) params.tenantId = filters.tenantId;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;

  return useQuery({
    queryKey: ['admin', 'campaign-usage', params],
    queryFn: () =>
      posBackendApi.get<{ data: CampaignUsageRow[]; meta: { count: number } }>(
        '/customer-engagement/campaigns/usage-summary',
        params,
      ),
  });
}
